<template>
  <div class="self-stretch flex flex-col gap-1 pb-2 mb-1 border-b border-border-separator" data-test="scheduled-result-card">
    <div class="self-stretch inline-flex justify-between items-center gap-2">
      <div class="font-sans text-sm font-bold text-text-default truncate" :title="taskName || fallback_name" data-test="task-name">
        {{ taskName || fallback_name }}
      </div>
      <!-- flex-shrink-0: the pill states the outcome and must never be the thing that truncates when a
           task has a long name. -->
      <div class="flex-shrink-0 px-2 py-0.5 rounded-full font-sans text-[10px] font-bold uppercase tracking-[0.06em]"
           :class="pill_class" data-test="outcome-pill">
        {{ pill_label }}
      </div>
    </div>
    <div class="self-stretch font-sans text-xs text-text-subtle" data-test="subline">
      <span v-if="run_at_label" data-test="run-at">{{ run_at_label }}</span>
      <span v-if="run_at_label && store_label" aria-hidden="true"> · </span>
      <span v-if="store_label" data-test="store-scope">{{ store_label }}</span>
    </div>
    <!-- NPM-225 A7: a fired watcher's card, computed in code on the server — the headline and figures never
         come from the model's prose. Each part renders only when its metadata is there, so every other
         result (and every watcher delivered before this) keeps the header band alone. Same markup as the
         brief's figures, so the two cards read alike. -->
    <div v-if="headline_text" class="self-stretch font-sans text-xs font-bold text-text-default mt-1" data-test="watcher-headline">{{ headline_text }}</div>
    <dl v-if="metric_rows.length" class="self-stretch mt-1 flex flex-col gap-0.5 mb-0" data-test="watcher-metric-rows">
      <div v-for="(row, i) in metric_rows" :key="`${i}_${row.label}`" class="flex items-baseline justify-between gap-3" data-test="watcher-metric-row">
        <dt class="font-sans text-xs text-text-subtle truncate">{{ row.label }}</dt>
        <dd class="font-sans text-xs font-bold text-text-default flex-shrink-0 mb-0">{{ row.value }}</dd>
      </div>
    </dl>
    <div v-if="definition_text" class="self-stretch font-sans text-xs text-text-subtle" data-test="watcher-definition">{{ definition_text }}</div>
    <div v-if="sources_text" class="self-stretch font-sans text-[10px] text-text-subtle" data-test="watcher-sources">
      {{ $t('messaging.scheduledCard.sources', { sources: sources_text }) }}
    </div>
  </div>
</template>

<script>
import { mapStores } from 'pinia';
import { useMainStore } from '@store/main';

/**
 * The identifying band at the top of a scheduled result in the Scheduled tasks thread: task name,
 * outcome pill, run time and store scope.
 *
 * It renders the card's HEADER, not the whole card. The message it sits inside already supplies the
 * card frame (`.avery-message` is a padded, rounded, outlined block) and the footer controls (copy,
 * feedback, Sources) — so a second frame here would draw a box inside a box, and a second feedback
 * row would give the same message two of them. What was missing was the header: which task produced
 * this, when, against which store, and how it went.
 *
 * Everything it renders comes from metadata stamped by delivery (see ScheduledResultMetadata in the
 * SDK), never from parsing the message body. Those four facts used to be sentences INSIDE the body,
 * which meant showing them as a header would have required reading them back out of prose.
 *
 * Absent metadata renders as absent, not as a guess: a message delivered before these keys existed
 * (every result already sitting in someone's main Rey thread) shows no subline rather than an
 * invented one, and the card degrades to a name and a pill.
 */
export default {
  name: 'ScheduledResultCard',
  props: {
    /** Task name, from metadata. Empty on a pre-metadata message. */
    taskName: {
      type: String,
      required: false,
      default: '',
    },
    /** ISO-8601 UTC string of when the RUN produced this — not when it was delivered. */
    runAt: {
      type: String,
      required: false,
      default: '',
    },
    /** RunStatus name: 'Delivered', 'NothingToReport', 'Failed' or 'AwaitingUser'. */
    outcome: {
      type: String,
      required: false,
      default: '',
    },
    /** CIF of the rooftop the task ran against. */
    dealerId: {
      type: String,
      required: false,
      default: '',
    },
    /** NPM-225 A7: a fired watcher's headline, computed in code. Empty on every other result. */
    headline: {
      type: String,
      required: false,
      default: '',
    },
    /** NPM-225 A7: the watcher's figures, as parsed `{label, value}` rows. */
    metrics: {
      type: Array,
      required: false,
      default: () => [],
    },
    /** NPM-225 A7: the condition the watcher checked, as the owner confirmed it. */
    definition: {
      type: String,
      required: false,
      default: '',
    },
    /** NPM-225 A7: the platforms the watcher's data came from. */
    sources: {
      type: String,
      required: false,
      default: '',
    },
  },
  computed: {
    ...mapStores(useMainStore),

    headline_text() {
      return (this.headline || '').trim();
    },
    definition_text() {
      return (this.definition || '').trim();
    },
    sources_text() {
      return (this.sources || '').trim();
    },
    /**
     * The watcher's figures, defensively, as BriefFindingCard reads its own: the rows arrive as parsed
     * metadata, so a row without a label is dropped rather than shown as a number nobody can interpret.
     */
    metric_rows() {
      if (!Array.isArray(this.metrics)) return [];
      return this.metrics
        .filter(row => row && typeof row.label === 'string' && row.label.trim())
        .map(row => ({
          label: row.label.trim(),
          value: row.value === null || row.value === undefined ? '' : String(row.value).trim(),
        }));
    },

    // Read the same way ReyConversationList reads it, so a store named in the drawer and the same
    // store named on a card cannot word themselves differently.
    all_dealers() {
      return this.mainStore.all_dealers || [];
    },

    /** Shown when a pre-metadata message has no task name to state. */
    fallback_name() {
      return this.$t('messaging.scheduledCard.untitledTask');
    },

    /**
     * The pill's wording. Falls back to the delivered label for an unrecognized status rather than
     * printing the raw enum name at a dealership manager — a value this client has not been taught
     * yet is still, by the fact that it arrived here, a result that was delivered.
     */
    pill_label() {
      switch (this.outcome) {
        case 'NothingToReport': return this.$t('messaging.scheduledCard.nothingToReport');
        case 'Failed': return this.$t('messaging.scheduledCard.failed');
        case 'AwaitingUser': return this.$t('messaging.scheduledCard.awaitingUser');
        default: return this.$t('messaging.scheduledCard.delivered');
      }
    },

    // Only a failure is coloured red. A delivered result and a quiet watcher are both the system working
    // as asked, and colouring "nothing to report" as a warning would teach people to read a normal
    // morning as a problem.
    //
    // An awaiting question is the one case that gets emphasis without alarm: it is not a fault — a task
    // that asks rather than guessing is behaving well — but it is the only outcome that needs something
    // FROM the reader, and a card nobody notices is a task that quietly never runs as asked.
    pill_class() {
      switch (this.outcome) {
        case 'Failed': return 'bg-danger/10 text-danger';
        case 'AwaitingUser': return 'bg-brand-subtle outline outline-1 outline-brand-outline text-text-link';
        case 'NothingToReport': return 'bg-transparent outline outline-1 outline-border-separator text-text-subtle';
        default: return 'bg-transparent outline outline-1 outline-brand-outline text-text-subtle';
      }
    },

    /**
     * The run time in the VIEWER's zone, via the browser's own formatter.
     *
     * Deliberately not the UTC string delivery stamps: the previous in-app format printed
     * "Data as of 2026-09-15 07:00 UTC" to an owner whose task fires on their own clock, so a 7am
     * recap announced itself as having run at a time the owner had never chosen.
     *
     * An unparseable or absent value renders nothing — see the note on the component.
     */
    run_at_label() {
      if (!this.runAt) return '';
      const when = new Date(this.runAt);
      if (Number.isNaN(when.getTime())) return '';

      try {
        return this.$t('messaging.scheduledCard.ranAt', {
          time: when.toLocaleString(undefined, {
            month: 'short', day: 'numeric', hour: 'numeric', minute: '2-digit',
          }),
        });
      } catch (e) {
        return '';
      }
    },

    /**
     * The rooftop's name, resolved case-insensitively on the CIF for the reason
     * ReyConversationList.resolveDealershipLabel documents: hosts do not agree on its casing, and a
     * strict match would cost the card its store name.
     *
     * Falls back to the raw CIF, which is still the honest answer for a store missing from the
     * caller's access list.
     */
    store_label() {
      if (!this.dealerId) return '';
      const dealer = this.all_dealers.find(
        candidate => (candidate.cifNumber || '').toLowerCase() === String(this.dealerId).toLowerCase());
      return dealer?.name || dealer?.cifNumber || this.dealerId;
    },
  },
};
</script>
